import { collection, getDocs } from 'firebase/firestore';
import { useEffect, useMemo, useState } from 'react';
import { useFirebase } from '../contexts/FirebaseContext';
import Fuse from 'fuse.js';

const SearchUtils = () => {
  const { db } = useFirebase();
  const [allClubs, setAllClubs] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [fuseInstance, setFuseInstance] = useState(null);

  // Configure Fuse options for optimal club searching
  const fuseOptions = {
    keys: ['clubName', 'city', 'country', 'county', 'searchableClubName'],
    threshold: 0.9, // Lower = stricter matching (0 is exact, 1 is match anything)
    distance: 50, // Allow for reasonable typos
    minMatchCharLength: 4,
    shouldSort: true,
    includeScore: true,
    location: 0, // Start matching at beginning of strings
    findAllMatches: true,
  };

  useEffect(() => {
    const fetchClubs = async () => {
      try {
        setIsLoading(true);
        const clubsRef = collection(db, 'clubs');
        const snapshot = await getDocs(clubsRef);
        const clubs = snapshot.docs.map((doc) => ({ ...doc.data(), id: doc.id }));
        setAllClubs(clubs);

        // Initialize Fuse instance with fetched clubs
        setFuseInstance(new Fuse(clubs, fuseOptions));
      } catch (error) {
        console.error('Error fetching clubs:', error);
      } finally {
        setIsLoading(false);
      }
    };
    fetchClubs();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const searchClubs = async (searchQuery) => {
    if (isLoading) {
      return []; // Return empty array while loading
    }

    if (!searchQuery) {
      return allClubs;
    }

    if (!fuseInstance) {
      return []; // Return empty array if Fuse isn't initialized yet
    }

    // Perform fuzzy search
    const results = fuseInstance.search(searchQuery);

    // Filter results to include only good matches
    const goodMatches = results
      .filter((result) => result.score < 0.4) // Only include good matches
      .map((result) => result.item);

    return goodMatches;
  };

  const utils = useMemo(
    () => ({
      searchClubs,
      isLoading,
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [isLoading]
  );

  return utils;
};

export function useSearchUtils() {
  return SearchUtils();
}
